import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  clampSliderIndex,
} from "../utils/tournamentProductUtils";

export default function useTournamentGallery({
  slides = [],
  defaultCategory = "all",
  autoPlay = true,
  interval = 5500,
} = {}) {
  const [category, setCategory] =
    useState(defaultCategory);

  const [index, setIndex] =
    useState(0);

  const [paused, setPaused] =
    useState(false);

  const [loaded, setLoaded] =
    useState(false);

  const [
    lightboxOpen,
    setLightboxOpen,
  ] = useState(false);

  const containerRef =
    useRef(null);

  const thumbnailsRef =
    useRef(null);

  const touchRef = useRef({
    x: 0,
    y: 0,
    active: false,
  });

  const filteredSlides =
    useMemo(() => {
      if (
        category === "all"
      ) {
        return slides;
      }

      return slides.filter(
        (slide) =>
          slide.category ===
          category,
      );
    }, [category, slides]);

  const total =
    filteredSlides.length;

  const currentSlide =
    filteredSlides[index] ||
    filteredSlides[0] ||
    null;

  const progressPercent =
    useMemo(() => {
      if (!total) {
        return 0;
      }

      return (
        ((index + 1) / total) *
        100
      );
    }, [index, total]);

  const goTo =
    useCallback(
      (nextIndex) => {
        setLoaded(false);

        setIndex(
          clampSliderIndex(
            nextIndex,
            total,
          ),
        );
      },
      [total],
    );

  const next =
    useCallback(() => {
      setLoaded(false);

      setIndex((current) =>
        clampSliderIndex(
          current + 1,
          total,
        ),
      );
    }, [total]);

  const previous =
    useCallback(() => {
      setLoaded(false);

      setIndex((current) =>
        clampSliderIndex(
          current - 1,
          total,
        ),
      );
    }, [total]);

  const changeCategory =
    useCallback((nextCategory) => {
      setCategory(nextCategory);
      setIndex(0);
      setLoaded(false);
    }, []);

  const openLightbox =
    useCallback(() => {
      setLightboxOpen(true);
    }, []);

  const closeLightbox =
    useCallback(() => {
      setLightboxOpen(false);
    }, []);

  useEffect(() => {
    if (
      index >= total &&
      total > 0
    ) {
      setIndex(0);
    }
  }, [index, total]);

  useEffect(() => {
    if (
      !autoPlay ||
      paused ||
      lightboxOpen ||
      total <= 1
    ) {
      return undefined;
    }

    const reduceMotion =
      window.matchMedia?.(
        "(prefers-reduced-motion: reduce)",
      )?.matches;

    if (reduceMotion) {
      return undefined;
    }

    const timerId =
      window.setInterval(() => {
        setLoaded(false);

        setIndex((current) =>
          clampSliderIndex(
            current + 1,
            total,
          ),
        );
      }, interval);

    return () => {
      window.clearInterval(
        timerId,
      );
    };
  }, [
    autoPlay,
    interval,
    lightboxOpen,
    paused,
    total,
  ]);

  useEffect(() => {
    if (
      !total ||
      !filteredSlides.length
    ) {
      return undefined;
    }

    const preloadImage = (
      targetIndex,
    ) => {
      const target =
        filteredSlides[
          clampSliderIndex(
            targetIndex,
            total,
          )
        ];

      if (!target?.src) {
        return;
      }

      const image =
        new Image();

      image.src = target.src;
    };

    preloadImage(index + 1);
    preloadImage(index - 1);

    return undefined;
  }, [
    filteredSlides,
    index,
    total,
  ]);

  useEffect(() => {
    const container =
      thumbnailsRef.current;

    if (!container) {
      return;
    }

    const activeThumbnail =
      container.querySelector(
        `[data-gallery-thumb="${index}"]`,
      );

    if (!activeThumbnail) {
      return;
    }

    const targetLeft =
      activeThumbnail.offsetLeft -
      container.clientWidth / 2 +
      activeThumbnail.offsetWidth / 2;

    container.scrollTo({
      left: Math.max(
        0,
        targetLeft,
      ),
      behavior: "smooth",
    });
  }, [index, category]);

  useEffect(() => {
    const handleKeyDown = (
      event,
    ) => {
      const focusInside =
        containerRef.current?.contains(
          document.activeElement,
        );

      if (
        !focusInside &&
        !lightboxOpen
      ) {
        return;
      }

      if (
        event.key ===
        "ArrowRight"
      ) {
        event.preventDefault();
        next();
      }

      if (
        event.key ===
        "ArrowLeft"
      ) {
        event.preventDefault();
        previous();
      }

      if (
        event.key ===
          "Escape" &&
        lightboxOpen
      ) {
        closeLightbox();
      }
    };

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [
    closeLightbox,
    lightboxOpen,
    next,
    previous,
  ]);

  useEffect(() => {
    if (!lightboxOpen) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [lightboxOpen]);

  const onTouchStart =
    useCallback((event) => {
      const touch =
        event.changedTouches?.[0] ||
        event.touches?.[0];

      if (!touch) {
        return;
      }

      touchRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        active: true,
      };
    }, []);

  const onTouchEnd =
    useCallback(
      (event) => {
        if (
          !touchRef.current
            .active
        ) {
          return;
        }

        const touch =
          event.changedTouches?.[0];

        if (!touch) {
          return;
        }

        const deltaX =
          touch.clientX -
          touchRef.current.x;

        const deltaY =
          touch.clientY -
          touchRef.current.y;

        touchRef.current.active =
          false;

        if (
          Math.abs(deltaX) >
            45 &&
          Math.abs(deltaX) >
            Math.abs(deltaY)
        ) {
          if (deltaX < 0) {
            next();
          } else {
            previous();
          }
        }
      },
      [next, previous],
    );

  return {
    category,
    index,
    paused,
    loaded,
    lightboxOpen,

    filteredSlides,
    currentSlide,
    total,
    progressPercent,

    containerRef,
    thumbnailsRef,

    setPaused,
    setLoaded,

    changeCategory,
    goTo,
    next,
    previous,

    openLightbox,
    closeLightbox,

    onTouchStart,
    onTouchEnd,
  };
}